import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ContentCopy as ContentCopyIcon,
  DriveFileMove as DriveFileMoveIcon,
  MoreVert
} from '@mui/icons-material';
import { clsx } from 'clsx';
import logger from '../../utils/logger';

/**
 * @typedef {Object} FileItemProps
 * @property {Object} file - The file object to display
 * @property {string} file.name - The name of the file
 * @property {string} [file.type] - The MIME type or 'folder' designation
 * @property {boolean} [file.isDirectory] - Whether the item is a directory
 * @property {string} [file.extension] - The file extension (optional)
 * @property {boolean} [selected] - Whether the item is selected
 * @property {'list' | 'grid'} [viewMode='list'] - The view mode
 * @property {'small' | 'medium' | 'large'} [gridSize='medium'] - Size of grid items
 * @property {function} [onClick] - Click handler for the item
 * @property {function} [onShare] - Share handler
 * @property {function} [onCopy] - Copy handler
 * @property {function} [onMove] - Move handler
 * @property {function} [onDelete] - Delete handler
 * @property {function} [onVaultMenuOpen] - Vault menu handler
 * @property {boolean} [allowMoveToVault] - Whether moving to vault is allowed
 * @property {boolean} [allowMoveFromVault] - Whether moving from vault is allowed
 */

/**
 * FileItem component for displaying file/folder items with actions
 * @param {FileItemProps} props
 */
const FileItem = ({
  file,
  selected = false,
  viewMode = 'list',
  gridSize = 'medium',
  onClick,
  onShare,
  onCopy,
  onMove,
  onDelete,
  onVaultMenuOpen,
  allowMoveToVault = false,
  allowMoveFromVault = false
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  if (!file?.name) {
    logger.error('FileItem: file object or file.name is missing');
    return null;
  }

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleItemClick = (event) => {
    event.stopPropagation();
    onClick?.(file);
  };

  const getFileIcon = () => {
    if (file.isDirectory || file.type === 'folder') {
      return <FolderIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
    }

    const extension = file.extension || file.name.split('.').pop()?.toLowerCase();

    if (file.type) {
      if (file.type.startsWith('image/')) {
        return <ImageIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
      if (file.type === 'application/pdf') {
        return <PdfIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
      if (file.type.includes('document') || file.type.includes('sheet') || file.type.includes('presentation')) {
        return <DocIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
    }

    if (extension) {
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        return <ImageIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
      if (extension === 'pdf') {
        return <PdfIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
      if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) {
        return <DocIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
      }
    }

    return <FileIcon fontSize={viewMode === 'grid' ? 'large' : 'medium'} />;
  };

  const menuItems = [
    {
      label: 'Share',
      icon: <ShareIcon />,
      onClick: () => {
        handleMenuClose();
        onShare?.(file);
      },
      show: !!onShare
    },
    {
      label: 'Copy',
      icon: <ContentCopyIcon />,
      onClick: () => {
        handleMenuClose();
        onCopy?.(file);
      },
      show: !!onCopy
    },
    {
      label: 'Move',
      icon: <DriveFileMoveIcon />,
      onClick: () => {
        handleMenuClose();
        onMove?.(file);
      },
      show: !!onMove
    },
    {
      label: allowMoveFromVault ? 'Move/Copy to Personal...' : 'Move/Copy to Vault...',
      icon: <LockIcon />,
      onClick: (event) => {
        handleMenuClose();
        onVaultMenuOpen?.(event, file);
      },
      show: !!onVaultMenuOpen && (allowMoveToVault || allowMoveFromVault)
    },
    {
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: () => {
        handleMenuClose();
        onDelete?.(file);
      },
      show: !!onDelete
    }
  ];

  const gridSizeClasses = {
    small: 'w-24 h-24',
    medium: 'w-32 h-32',
    large: 'w-40 h-40'
  };

  return (
    <div
      className={clsx(
        'relative flex items-center p-2 rounded-lg cursor-pointer transition-colors',
        viewMode === 'list' ? 'flex-row gap-4' : 'flex-col gap-2',
        viewMode === 'grid' && gridSizeClasses[gridSize],
        selected ? 'bg-blue-100' : 'hover:bg-gray-100'
      )}
      onClick={handleItemClick}
    >
      {getFileIcon()}
      
      <Typography
        variant={viewMode === 'grid' ? 'caption' : 'body2'}
        className={clsx(
          'flex-1',
          viewMode === 'grid' ? 'text-center truncate w-full' : 'truncate'
        )}
        title={file.name}
      >
        {file.name}
      </Typography>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        className={clsx(
          'absolute',
          viewMode === 'grid' ? 'top-1 right-1' : 'right-2'
        )}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {menuItems
          .filter(item => item.show)
          .map((item, index) => (
            <MenuItem
              key={index}
              onClick={item.onClick}
              className="min-w-[150px]"
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
};

export default FileItem;
